import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Button } from '../components/Button';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthProvider';
import useAxios from '../api/axios';
import { MUNICIPALITY_OPTIONS } from '../utils/courseLocationData';

const toCheckedValue = (value) => value === true || value === 1 || value === '1';

function MyProfile () {
  const navigate = useNavigate();
  const {auth, setAuth} = useAuth();
  const api = useAxios();
  const user = auth?.user || {};
  const displayName = user?.name || [user?.firstname, user?.surename || user?.surname].filter(Boolean).join(' ') || user?.email || 'Käyttäjä';

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    firstname: '',
    surname: '',
    email: '',
    phone: '',
    municipality: '',
    year: '',
    sex: '',
    contactShareConsent: false,
    privacyPolicyAccepted: false,
  });

  useEffect(() => {
    setForm({
      firstname: user?.firstname || '',
      surname: user?.surename || user?.surname || '',
      email: user?.email || '',
      phone: user?.phone || '',
      municipality: user?.municipality || '',
      year: user?.year || '',
      sex: user?.sex || '',
      contactShareConsent: toCheckedValue(user?.contactShareConsent),
      privacyPolicyAccepted: toCheckedValue(user?.privacyPolicyAccepted),
    });
  }, [user?.firstname, user?.surename, user?.surname, user?.email, user?.phone, user?.municipality, user?.year, user?.sex, user?.contactShareConsent, user?.privacyPolicyAccepted]);

  const handleFieldChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.firstname.trim()) {
      toast.error('Etunimi on pakollinen');
      return;
    }

    if (!form.surname.trim()) {
      toast.error('Sukunimi on pakollinen');
      return;
    }

    if (!form.municipality.trim()) {
      toast.error('Paikkakunta on pakollinen');
      return;
    }

    if (!form.email.trim()) {
      toast.error('Sähköposti on pakollinen');
      return;
    }

    if (!emailRegex.test(form.email)) {
      toast.error('Tarkista sähköpostiosoite');
      return;
    }

    if (form.year && Number.isNaN(Number(form.year))) {
      toast.error('Syntymävuoden tulee olla numero');
      return;
    }

    if (form.sex && !['Mies', 'Nainen'].includes(form.sex)) {
      toast.error('Valitse sukupuoli');
      return;
    }

    if (!form.contactShareConsent) {
      toast.error('Hyväksy yhteystietojen näyttäminen seuraavalle vastustajalle');
      return;
    }

    if (!form.privacyPolicyAccepted) {
      toast.error('Hyväksy tietosuojaseloste');
      return;
    }

    try {
      const response = await api.post(
        '/login.php?action=editUser',
        JSON.stringify({
          firstname: form.firstname.trim(),
          surename: form.surname.trim(),
          phone: form.phone.trim(),
          municipality: form.municipality.trim(),
          email: form.email.trim(),
          year: form.year === '' ? '' : Number(form.year),
          sex: form.sex || null,
          contactShareConsent: form.contactShareConsent,
          privacyPolicyAccepted: form.privacyPolicyAccepted,
        }),
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const returnedUser = response?.data?.user || {};
      const fullName = returnedUser?.name || [returnedUser?.firstname || form.firstname, returnedUser?.surename || returnedUser?.surname || form.surname].filter(Boolean).join(' ').trim();

      setAuth((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          ...returnedUser,
          firstname: returnedUser?.firstname ?? form.firstname,
          surename: returnedUser?.surename ?? returnedUser?.surname ?? form.surname,
          surname: returnedUser?.surname ?? returnedUser?.surename ?? form.surname,
          email: returnedUser?.email ?? form.email,
          phone: returnedUser?.phone ?? form.phone,
          municipality: returnedUser?.municipality ?? form.municipality,
          year: returnedUser?.year ?? form.year,
          sex: returnedUser?.sex ?? form.sex,
          contactShareConsent: returnedUser?.contactShareConsent ?? form.contactShareConsent,
          contactShareConsentAt: returnedUser?.contactShareConsentAt ?? prev.user?.contactShareConsentAt,
          privacyPolicyAccepted: returnedUser?.privacyPolicyAccepted ?? form.privacyPolicyAccepted,
          privacyPolicyAcceptedAt: returnedUser?.privacyPolicyAcceptedAt ?? prev.user?.privacyPolicyAcceptedAt,
          name: fullName || prev.user?.name
        }
      }));

      setIsEditing(false);
      toast.success(response?.data?.message || 'Käyttäjätiedot päivitetty onnistuneesti.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;

      if (!err?.response) {
        toast.error('Ei vastausta palvelimelta');
      } else {
        toast.error(message || 'Käyttäjätietojen päivittäminen epäonnistui');
      }
    }
  };

  const handleCancel = () => {
    setForm({
      firstname: user?.firstname || '',
      surname: user?.surename || user?.surname || '',
      email: user?.email || '',
      phone: user?.phone || '',
      municipality: user?.municipality || '',
      year: user?.year || '',
      sex: user?.sex || '',
      contactShareConsent: toCheckedValue(user?.contactShareConsent),
      privacyPolicyAccepted: toCheckedValue(user?.privacyPolicyAccepted),
    });
    setIsEditing(false);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 pb-10 pt-24">
      <div className="mb-6 rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur md:flex md:items-center md:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Oma profiili</p>
          <h1 className="mt-2 text-3xl font-black text-white md:text-4xl">{displayName}</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300 md:text-base">
            Hallitse omia käyttäjätietojasi, päivitä perustiedot ja siirry salasanan vaihtoon.
          </p>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 md:mt-0">
          {!isEditing ? (
            <Button name="Muokkaa" type="EDIT" action={() => setIsEditing(true)} />
          ) : (
            <>
              <Button name="Tallenna" type="PRIMARY" action={handleSave} />
              <Button name="Peruuta" type="SECONDARY" action={handleCancel} />
            </>
          )}
          <Button name="Muuta salasana" type="SECONDARY" action={() => navigate('/uusi-salasana')} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur">
          <h2 className="mb-4 text-xl font-bold text-white">Käyttäjän tiedot</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              label="Etunimi"
              value={form.firstname}
              onChange={handleFieldChange('firstname')}
              disabled={!isEditing}
              variant="filled"
              sx={{ backgroundColor: 'white', borderRadius: '8px' }}
            />
            <TextField
              label="Sukunimi"
              value={form.surname}
              onChange={handleFieldChange('surname')}
              disabled={!isEditing}
              variant="filled"
              sx={{ backgroundColor: 'white', borderRadius: '8px' }}
            />
            <TextField
              label="Sähköposti"
              value={form.email}
              onChange={handleFieldChange('email')}
              disabled={!isEditing}
              variant="filled"
              sx={{ backgroundColor: 'white', borderRadius: '8px' }}
            />
            <TextField
              label="Puhelin"
              value={form.phone}
              onChange={handleFieldChange('phone')}
              disabled={!isEditing}
              variant="filled"
              sx={{ backgroundColor: 'white', borderRadius: '8px' }}
            />
            <TextField
              label="Paikkakunta"
              value={form.municipality}
              onChange={handleFieldChange('municipality')}
              disabled={!isEditing}
              select
              variant="filled"
              sx={{ backgroundColor: 'white', borderRadius: '8px' }}
            >
              <MenuItem value="" disabled>Valitse paikkakunta</MenuItem>
              {MUNICIPALITY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Syntymävuosi"
              value={form.year}
              onChange={handleFieldChange('year')}
              disabled={!isEditing}
              type="number"
              variant="filled"
              sx={{ backgroundColor: 'white', borderRadius: '8px' }}
            />
            <TextField
              label="Sukupuoli"
              value={form.sex}
              onChange={handleFieldChange('sex')}
              disabled={!isEditing}
              select
              variant="filled"
              sx={{ backgroundColor: 'white', borderRadius: '8px' }}
            >
              <MenuItem value="">Ei valittu</MenuItem>
              <MenuItem value="Mies">Mies</MenuItem>
              <MenuItem value="Nainen">Nainen</MenuItem>
            </TextField>
          </div>

          <div className="mt-6 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-4 text-sm text-slate-200">
            <p className="font-semibold text-cyan-200">Yhteystietojen käyttö</p>
            <p className="mt-2 leading-6 text-slate-300">
              Puhelinnumerosi ja sähköpostiosoitteesi eivät ole yleisesti näkyvillä sivustolla. Ne näytetään vain
              seuraavalle vastustajallesi, jotta voitte sopia peliajan.
            </p>
          </div>

          <div className="mt-5 grid gap-1 rounded-2xl border border-white/10 bg-slate-950/40 p-4">
            <FormControlLabel
              control={(
                <Checkbox
                  checked={form.contactShareConsent}
                  onChange={(event) => setForm((prev) => ({ ...prev, contactShareConsent: event.target.checked }))}
                  disabled={!isEditing}
                  sx={{ color: 'rgba(103, 232, 249, 0.7)', '&.Mui-checked': { color: '#67e8f9' } }}
                />
              )}
              label={(
                <span className="text-sm leading-6 text-slate-200">
                  Annan luvan siihen, että puhelinnumeroni ja sähköpostiosoitteeni näytetään seuraavalle
                  vastustajalleni pelien sopimista varten.
                </span>
              )}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={form.privacyPolicyAccepted}
                  onChange={(event) => setForm((prev) => ({ ...prev, privacyPolicyAccepted: event.target.checked }))}
                  disabled={!isEditing}
                  sx={{ color: 'rgba(103, 232, 249, 0.7)', '&.Mui-checked': { color: '#67e8f9' } }}
                />
              )}
              label={(
                <span className="text-sm leading-6 text-slate-200">
                  Olen lukenut ja hyväksyn{' '}
                  <Link
                    to="/tietosuojaseloste"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-cyan-300 underline hover:text-cyan-200"
                    onClick={(event) => event.stopPropagation()}
                  >
                    tietosuojaselosteen
                  </Link>
                  .
                </span>
              )}
            />
          </div>
        </div>

        <div className="rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur">
          <h2 className="mb-4 text-xl font-bold text-white">Yhteenveto</h2>
          <div className="space-y-3 text-sm text-slate-300">
            <div className="rounded-2xl bg-white/5 p-3"><span className="block text-xs uppercase tracking-[0.25em] text-cyan-300">Nimi</span><span className="text-white">{displayName}</span></div>
            <div className="rounded-2xl bg-white/5 p-3"><span className="block text-xs uppercase tracking-[0.25em] text-cyan-300">Sähköposti</span><span className="text-white">{form.email || 'Ei asetettu'}</span></div>
            <div className="rounded-2xl bg-white/5 p-3"><span className="block text-xs uppercase tracking-[0.25em] text-cyan-300">Puhelin</span><span className="text-white">{form.phone || 'Ei asetettu'}</span></div>
            <div className="rounded-2xl bg-white/5 p-3"><span className="block text-xs uppercase tracking-[0.25em] text-cyan-300">Sukupuoli</span><span className="text-white">{form.sex || 'Ei asetettu'}</span></div>
            <div className="rounded-2xl bg-white/5 p-3"><span className="block text-xs uppercase tracking-[0.25em] text-cyan-300">Yhteystietojen jakolupa</span><span className="text-white">{form.contactShareConsent ? 'Hyväksytty' : 'Ei hyväksytty'}</span></div>
            <div className="rounded-2xl bg-white/5 p-3"><span className="block text-xs uppercase tracking-[0.25em] text-cyan-300">Tietosuojaseloste</span><span className="text-white">{form.privacyPolicyAccepted ? 'Hyväksytty' : 'Ei hyväksytty'}</span></div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default MyProfile;